import {
    useEffect,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    Platform,
    Animated,
    View
} from "react-native";
import type IBadgeLettersProps from "./type";
import stylesheet, {
    getBadgeLetterStyle
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useMotionTiming
} from "../../../../helpers/motion";
import {
    BADGE_JOINED_SCRIPT_PATTERN
} from "../../constant";
import Text from "../../../text";

const BadgeLetters: FC<IBadgeLettersProps> = ({
    customTheme,
    titleStyle,
    variant,
    color,
    title
}) => {
    const {
        durations,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        isReduceMotionEnabled,
        duration,
        easing
    } = useMotionTiming("iconAppear", customTheme);

    const isInitialRef = useRef(true);

    const letters = useMemo(() => BADGE_JOINED_SCRIPT_PATTERN.test(title) ? [
        title
    ] : Array.from(title), [
        title
    ]);

    const values = useMemo(() => letters.map(() => new Animated.Value(isInitialRef.current ? 1 : 0)), [
        letters
    ]);

    useEffect(() => {
        if(isInitialRef.current) {
            isInitialRef.current = false;

            return;
        }

        const animation = Animated.stagger(isReduceMotionEnabled ? 0 : durations.stagger, values.map((value) => Animated.timing(value, {
            useNativeDriver: Platform.OS !== "web",
            toValue: 1,
            duration,
            easing
        })));

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        durations.stagger,
        values
    ]);

    const webDirectionProps = Platform.OS === "web" ? {
        dir: "ltr"
    } : {};

    return <View
        {...webDirectionProps}
        style={stylesheet.container}
        accessibilityLabel={title}
        accessible={true}
    >
        {
            letters.map((letter, index) => {
                const value = values[index];

                if(!value) {
                    return null;
                }

                return <Animated.View
                    style={getBadgeLetterStyle({
                        isReduceMotionEnabled,
                        spaces,
                        value
                    })}
                    key={`${index}-${letter}`}
                    aria-hidden={true}
                >
                    <Text
                        customTheme={customTheme}
                        customColor={color}
                        style={titleStyle}
                        variant={variant}
                    >
                        {letter === " " ? "\u00A0" : letter}
                    </Text>
                </Animated.View>;
            })
        }
    </View>;
};
export default BadgeLetters;
